import { CallTranscript } from 'services/api/getCallTranscript';

import { FormattedQualifications, FormattedTranscriptSpeech } from 'contexts/LiveKitContext';
import { QualificationsResponse } from 'services/api/AgentCenterAPI';

export const createWebAudioRinging = () => {
    const audioContext = new window.AudioContext();

    // Resume audio context if it's suspended (required by some browsers)
    if (audioContext.state === 'suspended') {
        audioContext.resume();
    }

    // Create a more realistic ringing pattern
    const oscillator1 = audioContext.createOscillator();
    const oscillator2 = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator1.type = 'sine';
    oscillator1.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator2.type = 'sine';
    oscillator2.frequency.setValueAtTime(1000, audioContext.currentTime);

    oscillator1.connect(gainNode);
    oscillator2.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Create a ringing pattern: ring for 0.5s, pause for 0.2s, ring for 0.5s
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.5);
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.55);
    gainNode.gain.setValueAtTime(0, audioContext.currentTime + 0.7);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.75);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 1.25);
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 1.3);

    oscillator1.start(audioContext.currentTime);
    oscillator2.start(audioContext.currentTime);
    oscillator1.stop(audioContext.currentTime + 1.3);
    oscillator2.stop(audioContext.currentTime + 1.3);
};

export const createWebAudioBeeping = () => {
    try {
        // Create audio element for the phone call sound
        const audio = new Audio('/sound/dialing_tone.mp3');

        // Set audio properties
        audio.volume = 0.6; // Moderate volume
        audio.preload = 'auto';

        // Play the sound
        audio.play().catch((error) => {
            console.warn('Error playing phone call sound:', error);
        });
    } catch (error) {
        console.warn('Error creating phone call sound:', error);
    }
};

export const formatTranscriptData = (transcriptData: CallTranscript): FormattedTranscriptSpeech[] => {
    if (!transcriptData || !transcriptData.speeches) return [];

    return transcriptData.speeches
        .map((speech) => {
            // Convert timestamp from nanoseconds to milliseconds
            const timestamp = new Date(speech.timestamp_ns / 1000000).toLocaleTimeString();

            return {
                sender:
                    speech.speaker === 'assistant'
                        ? 'SLAI Agent'
                        : speech.speaker === 'user'
                          ? 'Client'
                          : speech.speaker,
                text: speech.text,
                timestamp,
                order: speech.order,
            };
        })
        .sort((a, b) => a.order - b.order);
};

const WEBSOCKET_CLOSE_REASON = {
    CLOSE_CONNECTION: 'Close websocket connection',
    NEW_CONNECTION_ESTABLISHED: 'New connection established',
    CONNECTION_CLOSED_BY_ADMIN: 'User set offline by administrator',
};

export const populateWarningMessage = ({ code, reason }: CloseEvent) => {
    if (code !== 1000) {
        return "Connection lost unexpectedly. You're offline. Go active to resume receiving calls.";
    }

    switch (reason) {
        case WEBSOCKET_CLOSE_REASON.CLOSE_CONNECTION:
            return "Connection closed. You're offline. Go active to resume receiving calls.";
        case WEBSOCKET_CLOSE_REASON.NEW_CONNECTION_ESTABLISHED:
            return 'Multiple sessions detected. To continue using this tab, please refresh the page and close any other open tabs.';
        case WEBSOCKET_CLOSE_REASON.CONNECTION_CLOSED_BY_ADMIN:
            return "Connection closed by administrator. You're offline. Go active to resume receiving calls.";
        default:
            return "Connection lost. You're offline. Go active to resume receiving calls.";
    }
};

export const formatQualifications = (qualifications: QualificationsResponse[], shouldFilterUnknown = true) => {
    return qualifications
        .map((qual) => {
            // Format key for readability but keep it simpler
            const readableKey = qual.key
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

            const isFalse =
                qual.value === 'false' || qual.value === '' || qual.value === '[]' || qual.value === 'NEGATIVE';

            return {
                key: qual.key,
                label: readableKey,
                value: qual.value,
                isTrue: !isFalse && qual.value !== 'NO_ANSWER' && qual.value !== 'UNKNOWN',
                isFalse,
                isUnknown: qual.value === 'NO_ANSWER' || qual.value === 'UNKNOWN',
            };
        })
        .filter(({ key, isUnknown }) => key !== 'state' && (!shouldFilterUnknown || !isUnknown))
        .sort((a, b) => {
            // Order: isTrue first, then isFalse, then isUnknown
            const order = (x: { isTrue: boolean; isFalse: boolean; isUnknown: boolean }) =>
                x.isTrue ? 0 : x.isFalse ? 1 : 2;
            return order(a) - order(b);
        });
};
